import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  CartState,
  OrderingTime,
  CheckDecrementPricingPayload,
  CheckDeletePricingPayload,
  CheckUpdatePricingPayload,
} from './cartTypes';
import { CartInitialState } from './initialState';
import {
  Cart,
  CartItem,
  CartReducerActionType,
  // CartResponseFields,
  // CartResponseFields,
  //  UpdateCartItemModel
} from '../../(routes)/online-ordering/types';
import {
  // getGiftCardFields,
  saveCartData,
  updateCartById,
  // syncCartItems,
  // normalizeLoyaltyApplied,
  getLoginCartData,
  getLocalStorageData,
  convertDateFormat,
} from 'apps/portal/online/context/CartContext/utils';
import {
  sortObjectKeys,
  //  updateCartItemsValidations
} from 'apps/portal/online/utils/UtilFunctions';
import { deepStrictEqual } from 'assert';
import { DealTerms, IDealDataResponse, UserGiftCard, OrderSource } from '@zeus/shared';

// ============================================================================
// ASYNC THUNKS
// ============================================================================

export const proceedToPaymentThunk = createAsyncThunk(
  'cart/proceedToPayment',
  async (
    payload: {
      businessId: number;
      tipAmount: number;
      tipPercentage: number;
      specialNotes: string;
      addcustomtip: boolean;
      scheduleConfiguration?: any;
      restaurantId: number;
      customerUsername: string;
      guestSessionId: string;
      orderType: string;
      orderSource: string;
    },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const {
        businessId,
        // tipAmount,
        // tipPercentage,
        specialNotes,
        // addcustomtip,
        scheduleConfiguration,
        restaurantId,
        // customerUsername,
        // guestSessionId,
        orderType,
        orderSource,
      } = payload;

      // Get cart from Redux state
      const state = getState() as any;
      const cart = state.cart.carts[businessId]?.cart;

      if (!cart) {
        return rejectWithValue({
          type: 'CART_NOT_FOUND',
          message: 'Cart not found in state',
        });
      }

      const data = getLocalStorageData();

      const restaurantAddress = data?.resturantAddress || {};
      const deliveryDate = data?.['deliveryDate'];
      const pickupDelDate = deliveryDate ? convertDateFormat(deliveryDate) : '';

      // Step 1: Normalize loyalty items and prepare cart for save
      // let localCart = { ...cart };
      // if (cart?.items) {
      //   const normalizedItems = normalizeLoyaltyApplied(cart.items);
      //   localCart = { ...localCart, items: normalizedItems } as Cart;
      // }

      // Remove gift card fields for payment
      const updatedCart = {
        ...cart,
        businessId,
        restaurantId: restaurantAddress.id,
        restaurantName: restaurantAddress.name || '',
        address: data?.userAddress || '',
        apartment: String(data?.appartmentAddress || ''),
        city: data?.city || '',
        state: data?.state || '',
        street: data?.street || '',
        zipCode: data?.zipcode || 0,
        latitude: data?.userLatitude || 0,
        longitude: data?.userLongitude || 0,
        resturantAddress: restaurantAddress,
        deliveryPickupDate: pickupDelDate,
        customerUsername: data.customerUsername,
        guestSessionId: data.guestSessionId,
        is_gift_card_applied: false,
        user_gift_card_id: undefined,
        giftCardAmount: undefined,
        giftCard: undefined,
      };

      if (!updatedCart?.isQr) {
        const orderingTime = state.cart.carts[businessId]?.orderingTime;
        if (businessId && orderingTime && orderingTime[businessId] && orderingTime[businessId].restaurant) {
          updatedCart.estimatedTimeSlot = orderingTime[businessId];
          updatedCart.estimatedTimeSlotId = orderingTime[businessId]?.id || 0;
        }
      }

      if (cart.id) {
        const updateResponse = await updateCartById({
          id: cart.id,
          tipAmount: updatedCart.tipAmount,
          tipPercentage: updatedCart.tipPercentage,
          note: specialNotes,
          order_type: cart?.order_type,
          ...scheduleConfiguration,
        });

        if (!updateResponse || updateResponse.status !== 200) {
          return rejectWithValue({
            type: 'UPDATE_ERROR',
            message: 'Failed to update cart',
          });
        }
      }

      // Step 2: Save cart to backend (prepareCart API)
      const saveResponse = await saveCartData(updatedCart);

      if (!saveResponse || !saveResponse.data) {
        return rejectWithValue({
          type: 'SAVE_ERROR',
          message: 'Failed to save cart',
        });
      }

      // Step 3: Extract cart ID from save response and set it in Redux
      const savedCartId = saveResponse.data?.cart?.id;
      if (!savedCartId) {
        return rejectWithValue({
          type: 'SAVE_ERROR',
          message: 'Cart ID not found in response',
        });
      }

      // Set global cart ID in Redux
      dispatch(updateCartSettings({ cartId: savedCartId }));

      // Step 4: Update cart with tip, notes, and schedule configuration using the saved cart ID
      // const tip = addcustomtip ? Number(tipAmount) : Number(tipPercentage?.toFixed(2));
      if (!cart.id) {
        const updateResponse = await updateCartById({
          id: savedCartId,
          tipAmount: updatedCart.tipAmount,
          tipPercentage: updatedCart.tipPercentage,
          note: specialNotes,
          order_type: cart?.order_type,
          ...scheduleConfiguration,
        });

        if (!updateResponse || updateResponse.status !== 200) {
          return rejectWithValue({
            type: 'UPDATE_ERROR',
            message: 'Failed to update cart',
          });
        }
      }

      // Step 5: Sync cart with remote data using getLoginCartData
      const syncedCart = await getLoginCartData(
        restaurantId,
        updatedCart.customerUsername,
        updatedCart.guestSessionId,
        orderType,
        orderSource
      );

      if (!syncedCart) {
        return rejectWithValue({
          type: 'SYNC_ERROR',
          message: 'Failed to sync cart',
        });
      }

      // Step 6: Process synced cart data
      const processedCart: any = { ...syncedCart } as Cart;

      let quantityFound = false;
      if ((processedCart && processedCart?.quantity) || processedCart?.quantity == 0) {
        quantityFound = true;
        processedCart['orderedQuantity'] = processedCart.quantity;
      }

      const items: CartItem[] = processedCart?.items || [];
      processedCart['discountedPrice'] = items.reduce((sum, item) => sum + Number(item?.discountedPrice ?? 0), 0) ?? 0;

      if (processedCart && processedCart.items) {
        for (const [index, item] of processedCart.items.entries()) {
          const cartItemId = item['id'];

          if (!quantityFound)
            processedCart['orderedQuantity'] += processedCart.orderedQuantity || processedCart.quantity || 0;
          let updatedItem = { ...item };

          if (updatedItem.item) {
            updatedItem = {
              ...updatedItem,
              ...(({
                actualPrice,
                orderedQuantity,
                isDiscount,
                discountPercentage,
                discountedPrice,
                paymentInfo,
                ...rest
              }) => rest)(updatedItem.item),
              ...(cart?.dealId
                ? {
                    discountedPrice: item.dealId
                      ? Math.max(
                          0,
                          item.actualPrice -
                            (item?.paymentInfo?.itemLevelDealDiscount ?? 0) * (item?.orderedQuantity ?? 0)
                        )
                      : item.actualPrice,
                  }
                : {}),
            };
          }

          if (updatedItem.metaData) {
            updatedItem = {
              ...updatedItem,
              ...(({
                actualPrice,
                orderedQuantity,
                isDiscount,
                discountPercentage,
                discountedPrice,
                paymentInfo,
                stockStatus,
                ...rest
              }) => rest)(updatedItem.metaData),
              cart_item_id: cartItemId,
              ...(cart?.dealId
                ? {
                    discountedPrice: item.dealId
                      ? Math.max(
                          0,
                          item.actualPrice -
                            (item?.paymentInfo?.itemLevelDealDiscount ?? 0) * (item?.orderedQuantity ?? 0)
                        )
                      : item.actualPrice,
                  }
                : {}),
            };

            delete updatedItem.metaData;
          }

          processedCart.items[index] = updatedItem;
        }
      }

      return {
        businessId,
        cart: {
          ...cart,
          ...processedCart,
          ...(cart?.dealId
            ? {
                discountedPrice:
                  processedCart.actualPrice -
                  ((processedCart?.paymentInfo?.discount ?? 0) - (processedCart?.paymentInfo?.entireDealDiscount ?? 0)),
              }
            : {}),
        },
        cartId: savedCartId,
        success: true,
      };
    } catch (error: any) {
      console.error('Error in proceedToPaymentThunk:', error);
      return rejectWithValue({
        type: 'UNKNOWN_ERROR',
        message: error?.message || 'An error occurred during payment processing',
      });
    }
  }
);

// ============================================================================
// SAVE AND SYNC CART THUNK
// ============================================================================

export const saveAndSyncCartThunk = createAsyncThunk(
  'cart/saveAndSyncCart',
  async (_, { rejectWithValue, getState }) => {
    try {
      // Get cart from Redux state
      const state = getState() as any;
      const businessId = state.cart.activeBusinessId;

      if (!businessId) {
        return rejectWithValue({
          type: 'NO_ACTIVE_BUSINESS',
          message: 'No active business ID found in state',
        });
      }

      const cart = state.cart.carts[businessId]?.cart;

      if (!cart) {
        return rejectWithValue({
          type: 'CART_NOT_FOUND',
          message: 'Cart not found in state',
        });
      }

      // Get local storage data
      const localData = getLocalStorageData();
      const { decryptData } = await import('apps/portal/online/utils/Crypto');
      const user = state.auth.user;

      // Extract credentials from local data and user state
      const customerUserName = decryptData(user?.customerUserName || '');
      const restaurantId = localData.resturantAddress.id;
      const guestSessionId = localData?.guestSessionId;
      const orderType = cart?.order_type || (await import('../../(routes)/online-ordering/types')).OrderType.Takeaway;

      // Validate required parameters
      if (!(customerUserName || localData.customerUsername) || !guestSessionId) {
        return rejectWithValue({
          type: 'MISSING_CREDENTIALS',
          message: 'Customer credentials and guest session ID are required',
        });
      }

      // Step 1: Update cart customer (save cart)
      const { updateCartCustomer } = await import('apps/portal/online/services/cart/Cart');
      const saveResponse = await updateCartCustomer(
        restaurantId,
        guestSessionId,
        customerUserName || localData.customerUsername,
        orderType
      );

      if (!saveResponse || !saveResponse.data) {
        return rejectWithValue({
          type: 'SAVE_ERROR',
          message: 'Failed to save cart',
        });
      }

      // Step 2: Extract and process cart from response
      let responseCart = saveResponse?.data?.data?.cart;

      if (!responseCart) {
        return rejectWithValue({
          type: 'INVALID_RESPONSE',
          message: 'Cart not found in response',
        });
      }

      // Step 3: Process cart metadata
      let quantityFound = false;
      if ((responseCart && responseCart?.quantity) || responseCart?.quantity == 0) {
        quantityFound = true;
        responseCart['orderedQuantity'] = responseCart.quantity;
      }

      const items: CartItem[] = responseCart?.items || [];
      if (responseCart) {
        responseCart['discountedPrice'] = items.reduce((sum, item) => sum + Number(item?.discountedPrice ?? 0), 0) ?? 0;
      }

      // Step 4: Process cart items
      if (responseCart && responseCart.items) {
        for (const [index, item] of responseCart.items.entries()) {
          const cartItemId = item['id'];

          if (!quantityFound) {
            responseCart['orderedQuantity'] += responseCart.orderedQuantity || responseCart.quantity || 0;
          }
          let updatedItem = { ...item };

          // Remove nested item fields
          if (updatedItem.item) {
            updatedItem = {
              ...updatedItem,
              ...(({ actualPrice, orderedQuantity, isDiscount, discountPercentage, discountedPrice, ...rest }) => rest)(
                updatedItem.item
              ),
              ...(responseCart?.dealId
                ? {
                    discountedPrice: item.dealId
                      ? Math.max(
                          0,
                          item.actualPrice -
                            (item?.paymentInfo?.itemLevelDealDiscount ?? 0) * (item?.orderedQuantity ?? 0)
                        )
                      : item.actualPrice,
                  }
                : {}),
            };
          }

          // Process metaData
          if (updatedItem.metaData) {
            updatedItem = {
              ...updatedItem,
              ...(({ actualPrice, orderedQuantity, isDiscount, discountPercentage, discountedPrice, ...rest }) => rest)(
                updatedItem.metaData
              ),
              cart_item_id: cartItemId,
              ...(responseCart?.dealId
                ? {
                    discountedPrice: item.dealId
                      ? Math.max(
                          0,
                          item.actualPrice -
                            (item?.paymentInfo?.itemLevelDealDiscount ?? 0) * (item?.orderedQuantity ?? 0)
                        )
                      : item.actualPrice,
                  }
                : {}),
            };

            delete updatedItem.metaData;
          }

          responseCart.items[index] = updatedItem;
        }
      }

      // Step 5: Extract user points from restaurant customers
      const restaurantCustomer = (responseCart?.customer?.restaurantCustomers || []).filter(
        (record: any) => record.restaurantId === restaurantId
      );

      if (responseCart) {
        responseCart['userPoints'] = restaurantCustomer.length ? restaurantCustomer[0]?.userPoints : 0;
      }

      // Step 6: Preserve deal information from current cart state
      responseCart = {
        ...responseCart,
        ...(responseCart?.dealId
          ? {
              discountedPrice:
                responseCart.actualPrice -
                ((responseCart?.paymentInfo?.discount ?? 0) - (responseCart?.paymentInfo?.entireDealDiscount ?? 0)),
            }
          : {}),
        ...(cart?.dealId && {
          dealId: cart?.dealId,
          deal: cart?.deal,
        }),
      };

      return {
        businessId,
        cart: { ...cart, ...responseCart },
        success: true,
      };
    } catch (error: any) {
      console.error('Error in saveAndSyncCartThunk:', error);
      return rejectWithValue({
        type: 'UNKNOWN_ERROR',
        message: error?.message || 'An error occurred while saving and syncing cart',
      });
    }
  }
);

// ============================================================================
// APPLY GIFT CARD THUNK
// ============================================================================

export const applyGiftCardThunk = createAsyncThunk(
  'cart/applyGiftCard',
  async (
    payload: {
      giftCardData: UserGiftCard;
      amountRedeemed: number;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const { giftCardData, amountRedeemed } = payload;

      // Get cart from Redux state
      const state = getState() as any;
      const businessId = state.cart.activeBusinessId;

      if (!businessId) {
        return rejectWithValue({
          type: 'NO_ACTIVE_BUSINESS',
          message: 'No active business ID found in state',
        });
      }

      const cart = state.cart.carts[businessId]?.cart;

      if (!cart) {
        return rejectWithValue({
          type: 'CART_NOT_FOUND',
          message: 'Cart not found in state',
        });
      }

      // Validate gift card data
      if (!giftCardData || !giftCardData.id) {
        return rejectWithValue({
          type: 'INVALID_GIFT_CARD',
          message: 'Invalid gift card data provided',
        });
      }

      if (amountRedeemed <= 0) {
        return rejectWithValue({
          type: 'INVALID_AMOUNT',
          message: 'Redemption amount must be greater than zero',
        });
      }

      // Get local storage data
      const localData = getLocalStorageData();
      const { decryptData } = await import('apps/portal/online/utils/Crypto');
      const user = state.auth.user;

      // Extract credentials from local data and user state
      const customerUserName = decryptData(user?.customerUserName || '');
      const restaurantId = localData.resturantAddress.id;
      const guestSessionId = localData?.guestSessionId;
      const orderType = cart?.order_type || (await import('../../(routes)/online-ordering/types')).OrderType.Takeaway;
      const orderSource = cart?.orderSource || OrderSource.Website;

      // Validate required parameters
      if (!(customerUserName || localData.customerUsername) || !guestSessionId) {
        return rejectWithValue({
          type: 'MISSING_CREDENTIALS',
          message: 'Customer credentials and guest session ID are required',
        });
      }

      // Step 1: Prepare cart with gift card fields
      const cartWithGiftCard = {
        ...cart,
        is_gift_card_applied: true,
        user_gift_card_id: giftCardData.id,
        giftCardAmount: amountRedeemed,
        giftCard: giftCardData,
        businessId,
        customerUsername: localData.customerUsername,
        guestSessionId: localData.guestSessionId,
      };

      // Step 2: Save cart to backend (prepareCart API)
      const saveResponse = await saveCartData(cartWithGiftCard);

      if (!saveResponse || !saveResponse.data) {
        return rejectWithValue({
          type: 'SAVE_ERROR',
          message: 'Failed to save cart with gift card',
        });
      }

      // Step 3: Fetch updated cart from remote
      const syncedCart = await getLoginCartData(
        restaurantId,
        customerUserName || localData.customerUsername,
        guestSessionId,
        orderType,
        orderSource
      );

      if (!syncedCart) {
        return rejectWithValue({
          type: 'SYNC_ERROR',
          message: 'Failed to sync cart from remote',
        });
      }

      // Step 4: Process synced cart data
      const processedCart: any = { ...syncedCart } as Cart;

      let quantityFound = false;
      if ((processedCart && processedCart?.quantity) || processedCart?.quantity == 0) {
        quantityFound = true;
        processedCart['orderedQuantity'] = processedCart.quantity;
      }

      const items: CartItem[] = processedCart?.items || [];
      processedCart['discountedPrice'] = items.reduce((sum, item) => sum + Number(item?.discountedPrice ?? 0), 0) ?? 0;

      if (processedCart && processedCart.items) {
        for (const [index, item] of processedCart.items.entries()) {
          const cartItemId = item['id'];

          if (!quantityFound)
            processedCart['orderedQuantity'] += processedCart.orderedQuantity || processedCart.quantity || 0;
          let updatedItem = { ...item };

          if (updatedItem.item) {
            updatedItem = {
              ...updatedItem,
              ...(({
                actualPrice,
                orderedQuantity,
                isDiscount,
                discountPercentage,
                discountedPrice,
                paymentInfo,
                ...rest
              }) => rest)(updatedItem.item),
              ...(cart?.dealId
                ? {
                    discountedPrice: item.dealId
                      ? Math.max(
                          0,
                          item.actualPrice -
                            (item?.paymentInfo?.itemLevelDealDiscount ?? 0) * (item?.orderedQuantity ?? 0)
                        )
                      : item.actualPrice,
                  }
                : {}),
            };
          }

          if (updatedItem.metaData) {
            updatedItem = {
              ...updatedItem,
              ...(({
                actualPrice,
                orderedQuantity,
                isDiscount,
                discountPercentage,
                discountedPrice,
                paymentInfo,
                stockStatus,
                ...rest
              }) => rest)(updatedItem.metaData),
              cart_item_id: cartItemId,
              ...(cart?.dealId
                ? {
                    discountedPrice: item.dealId
                      ? Math.max(
                          0,
                          item.actualPrice -
                            (item?.paymentInfo?.itemLevelDealDiscount ?? 0) * (item?.orderedQuantity ?? 0)
                        )
                      : item.actualPrice,
                  }
                : {}),
            };

            delete updatedItem.metaData;
          }

          processedCart.items[index] = updatedItem;
        }
      }

      // Step 5: Extract user points from restaurant customers
      const restaurantCustomer = (processedCart?.customer?.restaurantCustomers || []).filter(
        (record: any) => record.restaurantId === restaurantId
      );

      if (processedCart) {
        processedCart['userPoints'] = restaurantCustomer.length ? restaurantCustomer[0]?.userPoints : 0;
      }

      // Step 6: Preserve gift card and deal information
      const finalCart = {
        ...processedCart,
        is_gift_card_applied: true,
        user_gift_card_id: giftCardData.id,
        giftCardAmount: amountRedeemed,
        giftCard: giftCardData,
        ...(cart?.dealId
          ? {
              discountedPrice:
                processedCart.actualPrice -
                ((processedCart?.paymentInfo?.discount ?? 0) - (processedCart?.paymentInfo?.entireDealDiscount ?? 0)),
            }
          : {}),
        ...(cart?.dealId && {
          dealId: cart?.dealId,
          deal: cart?.deal,
        }),
      };

      return {
        businessId,
        cart: { ...cart, ...finalCart },
        success: true,
      };
    } catch (error: any) {
      console.error('Error in applyGiftCardThunk:', error);
      return rejectWithValue({
        type: 'UNKNOWN_ERROR',
        message: error?.message || 'An error occurred while applying gift card',
      });
    }
  }
);

// ============================================================================
// REMOVE GIFT CARD THUNK
// ============================================================================

export const removeGiftCardThunk = createAsyncThunk('cart/removeGiftCard', async (_, { rejectWithValue, getState }) => {
  try {
    // Get cart from Redux state
    const state = getState() as any;
    const businessId = state.cart.activeBusinessId;

    if (!businessId) {
      return rejectWithValue({
        type: 'NO_ACTIVE_BUSINESS',
        message: 'No active business ID found in state',
      });
    }

    const cart = state.cart.carts[businessId]?.cart;

    if (!cart) {
      return rejectWithValue({
        type: 'CART_NOT_FOUND',
        message: 'Cart not found in state',
      });
    }

    // Get local storage data
    const localData = getLocalStorageData();
    const { decryptData } = await import('apps/portal/online/utils/Crypto');
    const user = state.auth.user;

    // Extract credentials from local data and user state
    const customerUserName = decryptData(user?.customerUserName || '');
    const restaurantId = localData.resturantAddress.id;
    const guestSessionId = localData?.guestSessionId;
    const orderType = cart?.order_type || (await import('../../(routes)/online-ordering/types')).OrderType.Takeaway;
    const orderSource = cart?.orderSource || OrderSource.Website;

    // Validate required parameters
    if (!(customerUserName || localData.customerUsername) || !guestSessionId) {
      return rejectWithValue({
        type: 'MISSING_CREDENTIALS',
        message: 'Customer credentials and guest session ID are required',
      });
    }

    // Step 1: Prepare cart with gift card fields cleared
    const cartWithoutGiftCard = {
      ...cart,
      is_gift_card_applied: false,
      user_gift_card_id: null,
      giftCardAmount: 0,
      giftCard: null,
      businessId,
      customerUsername: localData.customerUsername,
      guestSessionId: localData.guestSessionId,
    };

    // Step 2: Save cart to backend (prepareCart API)
    const saveResponse = await saveCartData(cartWithoutGiftCard);

    if (!saveResponse || !saveResponse.data) {
      return rejectWithValue({
        type: 'SAVE_ERROR',
        message: 'Failed to save cart after removing gift card',
      });
    }

    // Step 3: Fetch updated cart from remote
    const syncedCart = await getLoginCartData(
      restaurantId,
      customerUserName || localData.customerUsername,
      guestSessionId,
      orderType,
      orderSource
    );

    if (!syncedCart) {
      return rejectWithValue({
        type: 'SYNC_ERROR',
        message: 'Failed to sync cart from remote',
      });
    }

    // Step 4: Process synced cart data
    const processedCart: any = { ...syncedCart } as Cart;

    let quantityFound = false;
    if ((processedCart && processedCart?.quantity) || processedCart?.quantity == 0) {
      quantityFound = true;
      processedCart['orderedQuantity'] = processedCart.quantity;
    }

    const items: CartItem[] = processedCart?.items || [];
    processedCart['discountedPrice'] = items.reduce((sum, item) => sum + Number(item?.discountedPrice ?? 0), 0) ?? 0;

    if (processedCart && processedCart.items) {
      for (const [index, item] of processedCart.items.entries()) {
        const cartItemId = item['id'];

        if (!quantityFound)
          processedCart['orderedQuantity'] += processedCart.orderedQuantity || processedCart.quantity || 0;
        let updatedItem = { ...item };

        if (updatedItem.item) {
          updatedItem = {
            ...updatedItem,
            ...(({
              actualPrice,
              orderedQuantity,
              isDiscount,
              discountPercentage,
              discountedPrice,
              paymentInfo,
              ...rest
            }) => rest)(updatedItem.item),
            ...(cart?.dealId
              ? {
                  discountedPrice: item.dealId
                    ? Math.max(
                        0,
                        item.actualPrice -
                          (item?.paymentInfo?.itemLevelDealDiscount ?? 0) * (item?.orderedQuantity ?? 0)
                      )
                    : item.actualPrice,
                }
              : {}),
          };
        }

        if (updatedItem.metaData) {
          updatedItem = {
            ...updatedItem,
            ...(({
              actualPrice,
              orderedQuantity,
              isDiscount,
              discountPercentage,
              discountedPrice,
              paymentInfo,
              stockStatus,
              ...rest
            }) => rest)(updatedItem.metaData),
            cart_item_id: cartItemId,
            ...(cart?.dealId
              ? {
                  discountedPrice: item.dealId
                    ? Math.max(
                        0,
                        item.actualPrice -
                          (item?.paymentInfo?.itemLevelDealDiscount ?? 0) * (item?.orderedQuantity ?? 0)
                      )
                    : item.actualPrice,
                }
              : {}),
          };

          delete updatedItem.metaData;
        }

        processedCart.items[index] = updatedItem;
      }
    }

    // Step 5: Extract user points from restaurant customers
    const restaurantCustomer = (processedCart?.customer?.restaurantCustomers || []).filter(
      (record: any) => record.restaurantId === restaurantId
    );

    if (processedCart) {
      processedCart['userPoints'] = restaurantCustomer.length ? restaurantCustomer[0]?.userPoints : 0;
    }

    // Step 6: Clear gift card fields and preserve deal information
    const finalCart = {
      ...processedCart,
      is_gift_card_applied: false,
      user_gift_card_id: null,
      giftCardAmount: 0,
      giftCard: null,
      ...(cart?.dealId
        ? {
            discountedPrice:
              processedCart.actualPrice -
              ((processedCart?.paymentInfo?.discount ?? 0) - (processedCart?.paymentInfo?.entireDealDiscount ?? 0)),
          }
        : {}),
      ...(cart?.dealId && {
        dealId: cart?.dealId,
        deal: cart?.deal,
      }),
    };

    return {
      businessId,
      cart: { ...cart, ...finalCart },
      success: true,
    };
  } catch (error: any) {
    console.error('Error in removeGiftCardThunk:', error);
    return rejectWithValue({
      type: 'UNKNOWN_ERROR',
      message: error?.message || 'An error occurred while removing gift card',
    });
  }
});

// ============================================================================
// PRICING CHECK THUNKS
// ============================================================================

export const checkDecrementPricingThunk = createAsyncThunk(
  'cart/checkDecrementPricing',
  async (payload: CheckDecrementPricingPayload, { getState }) => {
    const state = getState() as any;
    const businessId = state.cart.activeBusinessId;

    if (!businessId || !state.cart.carts[businessId]) {
      console.error('Cart not initialized for business:', businessId);
      return 0;
    }

    const { id, selectedModifierGroups } = payload;
    const cart = state.cart.carts[businessId].cart;

    if (!id) {
      return cart.discountedPrice;
    }

    const prevCartItems = cart.items;
    let discountedPrice = 0;

    for (const item of prevCartItems) {
      if (item.id === id || item.deal || item.dealId) {
        try {
          if (item.deal || item.dealId) {
            discountedPrice += Number(item.discountedPrice);
          } else {
            const sortedObj1 = sortObjectKeys(item.selectedModifierGroups);
            const sortedObj2 = sortObjectKeys(selectedModifierGroups);

            const sameModifiers = deepStrictEqual(JSON.stringify(sortedObj1), JSON.stringify(sortedObj2));

            if (item.isLoyaltyApplied) continue;

            if (sameModifiers == undefined) {
              discountedPrice += Number(item.discountedPrice) / Number(item.orderedQuantity || 1);
            }
          }
        } catch (error) {
          console.error('Items are not same!');
        }
      }
    }

    const newPrice = Number((Number(cart.discountedPrice) - discountedPrice).toFixed(2));
    return newPrice;
  }
);

export const checkDeletePricingThunk = createAsyncThunk(
  'cart/checkDeletePricing',
  async (payload: CheckDeletePricingPayload, { getState }) => {
    const state = getState() as any;
    const businessId = state.cart.activeBusinessId;

    if (!businessId || !state.cart.carts[businessId]) {
      console.error('Cart not initialized for business:', businessId);
      return 0;
    }

    const { id, itemIndex } = payload;
    const cart = state.cart.carts[businessId].cart;

    if (!id) {
      return cart.discountedPrice;
    }

    const prevCartItems = cart.items;
    let discountedPrice = 0;

    for (const [index, item] of prevCartItems.entries()) {
      if ((item.id === id && index === itemIndex) || item.deal || item.dealId) {
        discountedPrice += Number(item.discountedPrice);
      }
    }

    const newPrice = Number((Math.round((Number(cart.discountedPrice) - discountedPrice) * 100) / 100).toFixed(2));
    return newPrice;
  }
);

export const checkUpdatePricingThunk = createAsyncThunk(
  'cart/checkUpdatePricing',
  async (payload: CheckUpdatePricingPayload, { getState }) => {
    const state = getState() as any;
    const businessId = state.cart.activeBusinessId;

    if (!businessId || !state.cart.carts[businessId]) {
      console.error('Cart not initialized for business:', businessId);
      return 0;
    }

    const { updatedItem, oldCartItem } = payload;
    const cart = state.cart.carts[businessId].cart;

    const prevCartItems = cart.items;
    let discountedPrice = 0;

    for (const item of prevCartItems) {
      if (item.id === updatedItem?.id || item.deal || item.dealId) {
        if (item.deal || item.dealId) {
          discountedPrice += Number(item.discountedPrice);
        } else {
          try {
            const sortedObj1 = sortObjectKeys(oldCartItem.selectedModifierGroups);
            const sortedObj2 = sortObjectKeys(item.selectedModifierGroups);

            const sameModifiers = deepStrictEqual(JSON.stringify(sortedObj1), JSON.stringify(sortedObj2));

            if (
              (item.isLoyaltyApplied && !updatedItem.loyaltyAmountApplied) ||
              (!item.isLoyaltyApplied && updatedItem.loyaltyAmountApplied)
            ) {
              continue;
            }

            if (sameModifiers == undefined) {
              const prevOrderedQuantity = Number(item.orderedQuantity || 1);
              const prevPrice = Number(item.discountedPrice || 0) / prevOrderedQuantity;

              discountedPrice += prevPrice * prevOrderedQuantity;
            }
          } catch (error) {
            console.error('Items are not same!');
          }
        }
      }
    }

    const newPrice = Number(
      (
        Number(cart.discountedPrice) -
        discountedPrice +
        updatedItem.orderedQuantity * Number(updatedItem.discountedPrice)
      ).toFixed(2)
    );
    return newPrice;
  }
);

// ============================================================================
// INITIAL STATE

// ============================================================================

const initialState: CartState = {
  //New Implementation
  carts: {},
  activeBusinessId: null,
  isLoading: false,
  isSyncing: false,
  error: null,

  cartId: null,
  orderingTime: null,
  checkoutEdit: false,
  selectedScheduleType: 'ASAP',
  userCartItem: null,
  item: null,
  ticketIds: [],
  cartItem: null,
  checkoutErrors: [],
  isCartValid: true,
  deliveryFee: null,
};

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    initializeCart: (state, action: PayloadAction<{ businessId: number; restaurant: any }>) => {
      const { businessId, restaurant } = action.payload;

      if (!state.carts[businessId]) {
        state.carts[businessId] = {
          cart: CartInitialState,
          orderingTime: null,
          restaurant,
          lastUpdated: new Date().toISOString(),
          isDirty: false,
        };
      }

      state.activeBusinessId = businessId;
    },

    setOrderingTimeCart: (state, action: PayloadAction<{ businessId: number; orderingTime: OrderingTime }>) => {
      const { businessId, orderingTime } = action.payload;

      if (state.carts[businessId]) {
        state.carts[businessId].orderingTime = orderingTime;
        state.carts[businessId].lastUpdated = new Date().toISOString();
        state.carts[businessId].isDirty = true;
      }
    },

    setActiveCart: (state, action: PayloadAction<number>) => {
      state.activeBusinessId = action.payload;
    },

    addToCart: (state, action: PayloadAction<{ item?: CartItem }>) => {
      const businessId = state.activeBusinessId;

      if (!businessId || !state.carts[businessId]) {
        console.error('Cart not initialized for business:', businessId);
        return;
      }

      const cart = state.carts[businessId].cart;

      const updatedItem: CartItem = structuredClone(action.payload.item as CartItem);

      updatedItem['isSettingsVisible'] = true;

      // const giftCardFields = getGiftCardFields(cart);
      const prevCartItems = cart?.items || [];
      let isUpdateItem = false;
      const discountedPrice = Number(updatedItem.discountedPrice);

      for (const prevCartItem of prevCartItems) {
        if (
          prevCartItem.id === updatedItem?.id &&
          prevCartItem.special_instructions === updatedItem?.special_instructions
        ) {
          try {
            if (prevCartItem.isLoyaltyApplied !== updatedItem.isLoyaltyApplied) {
              continue;
            }

            // Check if dealId matches - items from deals should not merge with normal items
            const dealMismatch = (prevCartItem?.deal?.id || null) !== (updatedItem?.deal?.id || null);

            if (dealMismatch) {
              continue;
            }

            const sortedObj1 = sortObjectKeys(prevCartItem.selectedModifierGroups);
            const sortedObj2 = sortObjectKeys(updatedItem?.selectedModifierGroups);

            const sameModifiers = deepStrictEqual(JSON.stringify(sortedObj1), JSON.stringify(sortedObj2));

            if (prevCartItem.isLoyaltyApplied || updatedItem.isLoyaltyApplied) continue;

            if (sameModifiers == undefined && prevCartItem?.deal?.id === updatedItem?.deal?.id) {
              isUpdateItem = true;
              prevCartItem.discountedPrice =
                Number(prevCartItem.discountedPrice) + updatedItem.orderedQuantity * discountedPrice;
              prevCartItem.actualPrice =
                Number(prevCartItem.actualPrice) + updatedItem.orderedQuantity * discountedPrice;
              prevCartItem.orderedQuantity += updatedItem.orderedQuantity;

              break;
            }
          } catch (error) {
            console.error('Critical error comparing items:', error);
          }
        }
      }
      if (!isUpdateItem) {
        updatedItem.discountedPrice = updatedItem.orderedQuantity * discountedPrice;
        updatedItem.actualPrice = updatedItem.orderedQuantity * discountedPrice;
        cart.items.push(updatedItem);
        if (updatedItem.isLoyaltyApplied) {
          state.carts[businessId].cart.pointsUsed = updatedItem.loyaltyAmountApplied;
        }
      }

      state.carts[businessId].isDirty = true;
      state.carts[businessId].lastUpdated = new Date().toISOString();

      // Recalculate totals
      recalculateCartTotals(state.carts[businessId].cart);
    },

    updateToCart: (state, action: PayloadAction<{ updatedItem: CartItem; oldCartItem: CartItem }>) => {
      const { updatedItem, oldCartItem } = action.payload;

      const businessId = state.activeBusinessId;

      if (!businessId || !state.carts[businessId]) {
        console.error('Cart not initialized for business:', businessId);
        return;
      }

      const selectedCart = state.carts[businessId].cart;
      updatedItem.isSettingsVisible = true;

      const prevCartItems = [...(selectedCart.items || [])];
      const discountedPrice = Number(updatedItem.discountedPrice);
      // let prevOrderedQuantity = 0;
      // let prevPrice = 0;

      for (let i = 0; i < prevCartItems.length; i++) {
        const prevCartItem = prevCartItems[i];

        if (
          prevCartItem.id === updatedItem.id &&
          prevCartItem.special_instructions === oldCartItem.special_instructions
        ) {
          const sortedOld = sortObjectKeys(oldCartItem.selectedModifierGroups);
          const sortedPrev = sortObjectKeys(prevCartItem.selectedModifierGroups);
          const sameModifiers = JSON.stringify(sortedOld) === JSON.stringify(sortedPrev);

          const loyaltyMismatch =
            (prevCartItem.isLoyaltyApplied && !updatedItem.loyaltyAmountApplied) ||
            (!prevCartItem.isLoyaltyApplied && updatedItem.loyaltyAmountApplied);

          // Check if dealId matches - items from deals should not match normal items
          const dealMismatch = (prevCartItem?.deal?.id || null) !== (oldCartItem?.deal?.id || null);

          if (sameModifiers && !loyaltyMismatch && !dealMismatch) {
            // prevOrderedQuantity = Number(prevCartItem.orderedQuantity || 1);
            // prevPrice = Number(prevCartItem.discountedPrice || 0) / prevOrderedQuantity;

            prevCartItems[i] = {
              ...updatedItem,
              discountedPrice: updatedItem.orderedQuantity * discountedPrice,
              actualPrice: updatedItem.orderedQuantity * discountedPrice,
              orderedQuantity: updatedItem.orderedQuantity,
            };
            break;
          }
        }
      }

      const mergedItems: any[] = [];

      for (const item of prevCartItems) {
        const itemCopy = {
          ...item,
          selectedModifierGroups: sortObjectKeys({ ...item.selectedModifierGroups }),
        };
        const existingIndex = mergedItems.findIndex((mergedItem) => {
          const mergedItemCopy = {
            ...mergedItem,
            selectedModifierGroups: sortObjectKeys({ ...mergedItem.selectedModifierGroups }),
          };

          const loyaltyMismatch =
            (mergedItemCopy.isLoyaltyApplied && !itemCopy.isLoyaltyApplied) ||
            (!mergedItemCopy.isLoyaltyApplied && itemCopy.isLoyaltyApplied);

          if (loyaltyMismatch) {
            return false;
          }

          // Check if dealId matches - items from deals should not merge with normal items
          const dealMismatch = (mergedItemCopy?.deal?.id || null) !== (itemCopy?.deal?.id || null);

          if (dealMismatch) {
            return false;
          }

          // Check if dealItemType matches - buy and get items should not merge with each other
          const dealItemTypeMismatch = (mergedItemCopy?.dealItemType || null) !== (itemCopy?.dealItemType || null);

          if (dealItemTypeMismatch) {
            return false;
          }

          return (
            mergedItemCopy.id === itemCopy.id &&
            JSON.stringify(mergedItemCopy.selectedModifierGroups) === JSON.stringify(itemCopy.selectedModifierGroups) &&
            itemCopy.special_instructions === mergedItemCopy?.special_instructions
            // JSON.stringify(mergedItemCopy) === JSON.stringify(itemCopy)
          );
        });

        if (existingIndex !== -1) {
          const existing = mergedItems[existingIndex];
          const newQuantity = existing.orderedQuantity + item.orderedQuantity;
          const unitPrice = Number(item.discountedPrice) / item.orderedQuantity;

          mergedItems[existingIndex] = {
            ...existing,
            orderedQuantity: newQuantity,
            discountedPrice: unitPrice * newQuantity,
            actualPrice: unitPrice * newQuantity,
          };
        } else {
          mergedItems.push({ ...item });
        }
      }

      selectedCart.items = mergedItems;
      recalculateCartTotals(state.carts[businessId].cart);
    },
    incrementItemQuantity: (state, action: PayloadAction<{ item: CartItem; index: number; quantity?: number }>) => {
      const businessId = state.activeBusinessId;

      if (!businessId || !state.carts[businessId]) {
        console.error('Cart not initialized for business:', businessId);
        return;
      }

      const { item, index, quantity = 1 } = action.payload;

      const cart = state.carts[businessId].cart;

      const { id } = item;

      if (id) {
        // const giftCardFields = getGiftCardFields(cart);
        const prevCartItems = [...(cart?.items || [])];

        let netItemPrice = 0;
        let isDiscount = false;
        let discountPercentage = 0;
        let discountedPrice = 0;
        const cartItem = { ...prevCartItems[index] };
        netItemPrice = Number(cartItem.discountedPrice) / Number(cartItem.orderedQuantity || 1);
        isDiscount = cartItem.isDiscount;
        discountPercentage = Number(cartItem.discountPercentage);
        discountedPrice = isDiscount ? netItemPrice - netItemPrice * (discountPercentage / 100) : netItemPrice;

        cartItem.discountedPrice = Number(cartItem.discountedPrice) + discountedPrice * quantity;
        cartItem.actualPrice = Number(cartItem.actualPrice) + netItemPrice * quantity;
        cartItem.orderedQuantity += quantity;

        cart.items[index] = cartItem;
      } else {
        console.error('Item Id not found');
        return;
      }

      state.carts[businessId].isDirty = true;
      state.carts[businessId].lastUpdated = new Date().toISOString();

      recalculateCartTotals(cart);
    },

    decrementItemQuantity: (state, action: PayloadAction<{ item: CartItem; index: number; quantity?: number }>) => {
      const businessId = state.activeBusinessId;

      if (!businessId || !state.carts[businessId]) {
        console.error('Cart not initialized for business:', businessId);
        return;
      }

      const { item, index, quantity = 1 } = action.payload;

      const cart = state.carts[businessId].cart;

      const { id } = item;

      if (id) {
        // const giftCardFields = getGiftCardFields(cart);
        const prevCartItems = [...(cart.items || [])];

        const cartItem = { ...prevCartItems[index] };
        const currentQty = cartItem.orderedQuantity || 1;
        const decrementQty = Math.min(quantity, currentQty); // prevent over-decrement
        const netItemPrice = Number(cartItem.discountedPrice) / currentQty;
        const isDiscount = cartItem.isDiscount;
        const discountPercentage = Number(cartItem.discountPercentage);
        const discountedPrice = isDiscount ? netItemPrice - netItemPrice * (discountPercentage / 100) : netItemPrice;

        const totalDiscountedPrice = discountedPrice * decrementQty;
        const totalActualPrice = netItemPrice * decrementQty;

        cartItem.discountedPrice = Number(cartItem.discountedPrice) - totalDiscountedPrice;
        cartItem.actualPrice = Number(cartItem.actualPrice) - totalActualPrice;
        cartItem.orderedQuantity -= decrementQty;

        cart.items[index] = cartItem;
      } else {
        console.error('Item Id not found');
        return;
      }

      state.carts[businessId].isDirty = true;
      state.carts[businessId].lastUpdated = new Date().toISOString();

      recalculateCartTotals(cart);
    },

    deleteItem: (state, action: PayloadAction<{ item: CartItem; index: number; quantity?: number }>) => {
      const businessId = state.activeBusinessId;

      if (!businessId || !state.carts[businessId]) {
        console.error('Cart not initialized for business:', businessId);
        return;
      }

      const {
        item,
        index,
        // quantity = 1
      } = action.payload;

      const itemIndex = index;

      const cart = state.carts[businessId].cart;

      const { id } = item;

      if (id) {
        // const giftCardFields = getGiftCardFields(cart);
        const prevCartItems = cart?.items;

        // let netItemPrice = 0;
        // let isDiscount = false;
        // let discountPercentage = 0;
        // let discountedPrice = 0;

        for (const [index, item] of prevCartItems.entries()) {
          if (item.id === id && index === itemIndex) {
            // netItemPrice = Number(item.discountedPrice);
            // isDiscount = item.isDiscount;
            // discountPercentage = Number(item.discountPercentage);
            // discountedPrice = isDiscount ? netItemPrice - netItemPrice * (discountPercentage / 100) : netItemPrice;

            if (item.isLoyaltyApplied) cart.pointsUsed = 0;

            break;
          }
        }
        const filteredItems = prevCartItems.filter((_, index) => index !== itemIndex);
        if (filteredItems.length === 0) {
          cart.items = [];
        } else {
          cart.items = filteredItems;
        }
      } else {
        console.error('Item Id not found');
        return;
      }

      state.carts[businessId].isDirty = true;
      state.carts[businessId].lastUpdated = new Date().toISOString();

      recalculateCartTotals(cart);
    },

    removeItem: (state, action: PayloadAction<{ businessId: number; itemIndex: number }>) => {
      const { businessId, itemIndex } = action.payload;

      if (state.carts[businessId]) {
        state.carts[businessId].cart.items.splice(itemIndex, 1);
        state.carts[businessId].isDirty = true;
        state.carts[businessId].lastUpdated = new Date().toISOString();
        recalculateCartTotals(state.carts[businessId].cart);
      }
    },

    setState: (state, action: PayloadAction<Partial<Cart>>) => {
      const businessId = state.activeBusinessId;

      if (!businessId || !state.carts[businessId]) {
        console.warn('Cannot setState: No active cart initialized');
        return;
      }

      Object.assign(state.carts[businessId].cart, action.payload);
      state.carts[businessId].isDirty = true;
      state.carts[businessId].lastUpdated = new Date().toISOString();
    },

    updateItemQuantity: (state, action: PayloadAction<{ businessId: number; itemIndex: number; quantity: number }>) => {
      const { businessId, itemIndex, quantity } = action.payload;

      if (!state.carts[businessId]) {
        console.error('Cart not initialized for business:', businessId);
        return;
      }

      const cart = state.carts[businessId].cart;
      const item = cart.items[itemIndex];

      if (!item) {
        console.error('Item not found at index:', itemIndex);
        return;
      }

      if (!item.noMaxLimit && item.maxLimit && quantity > item.maxLimit) {
        console.warn('Max limit exceeded for item:', item.itemName);
        return;
      }

      if (item.minLimit && quantity < item.minLimit && quantity !== 0) {
        console.warn('Min limit not met for item:', item.itemName);
        return;
      }

      if (quantity <= 0) {
        cart.items.splice(itemIndex, 1);
      } else {
        item.orderedQuantity = quantity;
      }

      state.carts[businessId].isDirty = true;
      state.carts[businessId].lastUpdated = new Date().toISOString();

      recalculateCartTotals(cart);
    },

    resetCart: (state) => {
      const businessId = state.activeBusinessId;

      if (!businessId || !state.carts[businessId]) {
        console.error('Cart not initialized for business:', businessId);
        return;
      }

      const resetCartData: Partial<Cart> = {
        note: '',
        actualPrice: 0,
        discountedPrice: 0,
        orderedQuantity: 0,
        quantity: 0,
        totalBill: 0,
        items: [],
        tipAmount: 0,
        tipPercentage: 0,
        totalTax: 0,
        pointsUsed: 0,
        is_gift_card_applied: false,
        user_gift_card_id: undefined,
        giftCardAmount: 0,
        giftCard: undefined,
        id: undefined,
        error: undefined,
        deal: undefined,
        dealId: undefined,
      };
      const selectedCart = state.carts[businessId].cart;
      state.carts[businessId].cart = { ...selectedCart, ...resetCartData };
      state.carts[businessId].lastUpdated = new Date().toISOString();
      state.carts[businessId].isDirty = false;
    },

    addDealToCart: (state, action: PayloadAction<any>) => {
      const businessId = state.activeBusinessId;

      if (!businessId || !state.carts[businessId]) {
        console.error('Cart not initialized for business:', businessId);
        return;
      }

      const cart = state.carts[businessId].cart;

      const dealPayload = action.payload;
      const deal: IDealDataResponse = dealPayload?.deal as IDealDataResponse;
      let dealItems: CartItem[] = dealPayload?.dealItems as CartItem[];

      const items = cart?.items || [];
      const filteredIndexes: number[] = [];
      let actualPriceDeducted = 0;
      let discountedPriceDeducted = 0;
      let orderedQuantityDeducted = 0;
      let actualPriceAdded = 0;
      let discountedPriceAdded = 0;
      let orderedQuantityAdded = 0;

      const dealExists = items.some((item: any) => item?.dealId && item?.deal);
      const sameDealExists = items.some((item: any) => Number(item?.dealId) === Number(deal?.id));
      const dontAllowDealExists = items.some(
        (item: any) => item?.dealId && item?.deal && !item.deal.definedTerms.includes(DealTerms.USE_WITH_OTHERS)
      );

      if (
        sameDealExists ||
        dontAllowDealExists ||
        (dealExists && !deal?.definedTerms.includes(DealTerms.USE_WITH_OTHERS))
      ) {
        if (sameDealExists) {
          items.forEach((item: any, index: number) => {
            if (Number(item?.dealId) === Number(deal?.id) || Number(item?.deal?.id) === Number(deal?.id)) {
              filteredIndexes.push(index);
            }
          });
        } else if (dontAllowDealExists || (dealExists && !deal?.definedTerms.includes(DealTerms.USE_WITH_OTHERS))) {
          items.forEach((item: any, index: number) => {
            if (item?.dealId || item?.deal || item?.dealItemType) {
              filteredIndexes.push(index);
            }
          });
        }
      }

      for (const [index, item] of items.entries()) {
        if (filteredIndexes.includes(index)) {
          actualPriceDeducted += Number(item.actualPrice);
          discountedPriceDeducted += Number(item.discountedPrice);
          orderedQuantityDeducted += Number(item.orderedQuantity);
        }
      }
      const filteredItems = items.filter((_, index) => !filteredIndexes.includes(index));

      dealItems = (dealItems || []).map((item: any) => {
        const newItem = structuredClone(item);
        if (item?.dealItemType && item?.dealItemType === 'buy') {
          delete newItem?.dealId;
        }

        actualPriceAdded += Number(item.actualPrice);
        discountedPriceAdded += Number(item.discountedPrice);
        orderedQuantityAdded += Number(item.orderedQuantity);

        return newItem;
      });

      state.carts[businessId].cart.items = [...filteredItems, ...dealItems];
      state.carts[businessId].isDirty = true;
      state.carts[businessId].lastUpdated = new Date().toISOString();

      recalculateCartTotals(cart);
    },

    removeDealFromCart: (state, action: PayloadAction<any>) => {
      const businessId = state.activeBusinessId;

      if (!businessId || !state.carts[businessId]) {
        console.error('Cart not initialized for business:', businessId);
        return;
      }

      const cart = state.carts[businessId].cart;

      const dealPayload = action.payload;
      const dealIds = dealPayload.dealIds;

      const items = cart?.items ? JSON.parse(JSON.stringify(cart.items)) : [];
      const filteredIndexes: number[] = [];
      let actualPriceDeducted = 0;
      let discountedPriceDeducted = 0;
      let orderedQuantityDeducted = 0;
      let actualPriceAdded = 0;
      let discountedPriceAdded = 0;
      let orderedQuantityAdded = 0;

      items.forEach((item: any, index: number) => {
        if (
          dealIds.includes(Number(item?.dealId)) ||
          dealIds.includes(Number(item?.deal?.id)) ||
          (dealPayload?.actionType === CartReducerActionType.DeleteItem &&
            item.id === dealPayload.id &&
            index === dealPayload.itemIndex)
        ) {
          filteredIndexes.push(index);
        }
      });

      for (const [index, item] of items.entries()) {
        if (filteredIndexes.includes(index)) {
          actualPriceDeducted += Number(item.actualPrice);
          discountedPriceDeducted += Number(item.discountedPrice);
          orderedQuantityDeducted += Number(item.orderedQuantity);
        } else if (
          dealPayload?.actionType === CartReducerActionType.DecrementInCart &&
          dealPayload?.updatedItem &&
          dealPayload?.updatedItem?.id &&
          dealPayload?.updatedItem?.id === item.id
        ) {
          try {
            const sortedObj1 = sortObjectKeys(item.selectedModifierGroups);
            const sortedObj2 = sortObjectKeys(dealPayload?.updatedItem?.selectedModifierGroups);

            const sameModifiers = deepStrictEqual(JSON.stringify(sortedObj1), JSON.stringify(sortedObj2));

            if (item.isLoyaltyApplied) continue;

            if (sameModifiers == undefined) {
              const netItemPrice = Number(item.discountedPrice) / Number(item.orderedQuantity || 1);
              actualPriceDeducted += netItemPrice;
              discountedPriceDeducted += netItemPrice;
              orderedQuantityDeducted += 1;

              item.discountedPrice = Number(item.discountedPrice) - netItemPrice;
              item.actualPrice = Number(item.actualPrice) - netItemPrice;
              item.orderedQuantity -= 1;
            }
          } catch (error) {
            console.error('Items are not same!');
          }
        } else if (
          dealPayload?.actionType === CartReducerActionType.UpdateInCart &&
          dealPayload?.updatedItem &&
          dealPayload?.oldCartItem &&
          dealPayload?.updatedItem?.id === item.id
        ) {
          try {
            const sortedObj1 = sortObjectKeys(item.selectedModifierGroups);
            const sortedObj2 = sortObjectKeys(dealPayload?.oldCartItem?.selectedModifierGroups);

            const sameModifiers = deepStrictEqual(JSON.stringify(sortedObj1), JSON.stringify(sortedObj2));

            if (
              (item.isLoyaltyApplied && !dealPayload?.updatedItem.loyaltyAmountApplied) ||
              (!item.isLoyaltyApplied && dealPayload?.updatedItem.loyaltyAmountApplied)
            ) {
              continue;
            }

            if (sameModifiers == undefined) {
              const prevOrderedQuantity = Number(item.orderedQuantity || 1);
              const prevPrice = Number(item.discountedPrice || 0) / prevOrderedQuantity;

              actualPriceDeducted += prevPrice * prevOrderedQuantity;
              discountedPriceDeducted += prevPrice * prevOrderedQuantity;
              orderedQuantityDeducted += prevOrderedQuantity;

              items[index] = {
                ...dealPayload?.updatedItem,
                discountedPrice:
                  dealPayload?.updatedItem?.orderedQuantity * Number(dealPayload?.updatedItem?.discountedPrice),
                actualPrice:
                  dealPayload?.updatedItem?.orderedQuantity * Number(dealPayload?.updatedItem?.discountedPrice),
                orderedQuantity: dealPayload?.updatedItem?.orderedQuantity,
              };
            }
          } catch (error) {
            console.error('Items are not same!');
          }
        }
      }
      if (
        dealPayload?.actionType === CartReducerActionType.UpdateInCart &&
        dealPayload?.updatedItem &&
        dealPayload?.oldCartItem
      ) {
        actualPriceAdded +=
          dealPayload?.updatedItem?.orderedQuantity * Number(dealPayload?.updatedItem?.discountedPrice);
        discountedPriceAdded +=
          dealPayload?.updatedItem?.orderedQuantity * Number(dealPayload?.updatedItem?.discountedPrice);
        orderedQuantityAdded += dealPayload?.updatedItem?.orderedQuantity;
      }

      const filteredItems = items.filter((_: CartItem, index: number) => !filteredIndexes.includes(index));

      state.carts[businessId].cart.items = [...filteredItems];

      if (cart?.dealId) {
        state.carts[businessId].cart.dealId = dealIds.includes(Number(cart?.dealId || cart?.deal?.id))
          ? undefined
          : cart?.dealId;
        state.carts[businessId].cart.deal = dealIds.includes(Number(cart?.dealId || cart?.deal?.id))
          ? undefined
          : cart?.deal;
      }

      state.carts[businessId].isDirty = true;
      state.carts[businessId].lastUpdated = new Date().toISOString();
      recalculateCartTotals(cart);
    },

    // Legacy Reducers
    updateOrderingTime: (state, action: PayloadAction<any>) => {
      state.orderingTime = action.payload.orderingTime;
    },

    updateCartSettings: (state, action: PayloadAction<any>) => {
      for (const key in action?.payload) {
        (state as any)[key] = action?.payload[key];
      }
    },

    setCheckoutErrors: (state, action: PayloadAction<any>) => {
      state.checkoutErrors = action.payload.checkoutErrors;
      state.isCartValid = action.payload.isCartValid;
    },

    resetCartSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Proceed to Payment Thunk
      .addCase(proceedToPaymentThunk.pending, (state) => {
        state.isLoading = true;
        state.isSyncing = true;
        state.error = null;
      })
      .addCase(proceedToPaymentThunk.fulfilled, (state, action) => {
        const { businessId, cart, cartId } = action.payload;

        if (state.carts[businessId]) {
          state.carts[businessId].cart = cart;
          state.carts[businessId].lastUpdated = new Date().toISOString();
          state.carts[businessId].isDirty = false;
        }

        // Also set cartId in legacy state
        if (cartId) {
          state.cartId = cartId;
        }

        state.isLoading = false;
        state.isSyncing = false;
        state.error = null;
      })
      .addCase(proceedToPaymentThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.isSyncing = false;
        state.error = action.payload as string;
      })
      // Save and Sync Cart Thunk
      .addCase(saveAndSyncCartThunk.pending, (state) => {
        state.isLoading = true;
        state.isSyncing = true;
        state.error = null;
      })
      .addCase(saveAndSyncCartThunk.fulfilled, (state, action) => {
        const { businessId, cart } = action.payload;

        if (state.carts[businessId]) {
          state.carts[businessId].cart = cart;
          state.carts[businessId].lastUpdated = new Date().toISOString();
          state.carts[businessId].isDirty = false;
        }

        state.isLoading = false;
        state.isSyncing = false;
        state.error = null;
      })
      .addCase(saveAndSyncCartThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.isSyncing = false;
        state.error = action.payload as string;
      })
      // Apply Gift Card Thunk
      .addCase(applyGiftCardThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(applyGiftCardThunk.fulfilled, (state, action) => {
        const { businessId, cart } = action.payload;

        if (state.carts[businessId]) {
          state.carts[businessId].cart = cart;
          state.carts[businessId].lastUpdated = new Date().toISOString();
          state.carts[businessId].isDirty = false;
        }

        state.isLoading = false;
        state.error = null;
      })
      .addCase(applyGiftCardThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Remove Gift Card Thunk
      .addCase(removeGiftCardThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeGiftCardThunk.fulfilled, (state, action) => {
        const { businessId, cart } = action.payload;

        if (state.carts[businessId]) {
          state.carts[businessId].cart = cart;
          state.carts[businessId].lastUpdated = new Date().toISOString();
          state.carts[businessId].isDirty = false;
        }

        state.isLoading = false;
        state.error = null;
      })
      .addCase(removeGiftCardThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

// Helper function
function recalculateCartTotals(cart: Cart) {
  cart.quantity = cart.items.reduce((acc, current) => acc + current.orderedQuantity, 0);
  cart.orderedQuantity = cart.items.reduce((sum, item) => sum + item.orderedQuantity, 0);
  cart.actualPrice = cart.items.reduce((sum, item) => sum + (item.actualPrice || 0), 0);
  cart.discountedPrice = cart.items.reduce((sum, item) => sum + (item.discountedPrice || 0), 0);
}

export const {
  updateOrderingTime,
  updateCartSettings,
  resetCart,
  initializeCart,
  setActiveCart,
  setOrderingTimeCart,
  addToCart,
  updateToCart,
  deleteItem,
  removeItem,
  setState,
  incrementItemQuantity,
  decrementItemQuantity,
  updateItemQuantity,
  setCheckoutErrors,
  resetCartSlice,
  addDealToCart,
  removeDealFromCart,
} = cartSlice.actions;

export default cartSlice.reducer;
